import {EventEmitter} from "events";
import axios, { AxiosInstance, AxiosResponse } from "axios"
import Crypto, { RSA } from "../Crypto";
import jwtDecode from "jwt-decode"

const PUBLIC_PEM = `-----BEGIN PUBLIC KEY-----
MIICIjANBgkqhkiG9w0BAQEFAAOCAg8AMIICCgKCAgEAzI4rzXzGBPFrAIj0VH53
UJ0dLl9BNeD/JkBk4FAek+YZ0Eyx17WUyMrixWk6Ihg5oTbRN6wCxVaKTGJqGjvv
zOgbobpPKPJlQkFOxMQFH4dsKnXuEG0sy2Ib1BjxRfFyKjC9mRgltKTEyp5qCmSg
B6chrKiVKFbjYJBRNV8gpwViMvUISNBijh+/SnTKFtdxWqPstYx0b3jLvklnOLFI
vCufdxsmRrQi99VB/8aPElP3MN7+C/eWDbXH5+8d5WWCH0viDtilImZSvsQg41BG
cOwlVdwYFx/1hShi/5IncKfW92ZhUgCUoZ+kaZdpEuZGYN0ODMJqmzufGqpvqeHm
7/SlaBd8j5M6YKZkB5uSmUIb3C/ZX+pOub9qm/W25FEwNYXv2hfPBV7PjP7Uzs3q
BbSpCC+gM2qa0Scc4q0Ro75bsXxidhqQ0TzG9sH+HEhIbTim9rt62PXKdg/qxEfR
sndsN7oQagN4JkW6/lmWoBLS7VBK4JzSa1u/Qt0h12KyCLhQKGhLeQF+z62TzkXl
iwCkiXSQVFUGSHniUcOIOZP1W7CIkupjiw6Pb9T212W3HIil9mVcsFKsMXi5wPd9
JPrafnNYAEwpsLwk+F1uiLz1vUeEqtzN5rzJZ91VlCRVAFnP99H9rfx+1YBmEwza
iH9y2Nj1rFma7q466iJfj58CAwEAAQ==
-----END PUBLIC KEY-----`

const KEY = "user"

export interface IUsername {
    _id:string
    name:string
}

export interface IUser extends IUsername{
    email:string
    iat:number
    exp:number
}

export default class JWTApi extends EventEmitter {
    private _crypto = new RSA()
    private _api : AxiosInstance
    private _user : IUser|null = null
    
    constructor(api?:string){
        super()
        let baseURL:string
        if (api) baseURL = api
        else if (document.location.hostname=="localhost") baseURL = "http://localhost:3000"
        else baseURL = `https://${document.location.hostname}`
        this._api = axios.create({baseURL})
        this._api.interceptors.response.use(res => res, this.onError.bind(this))
        ;(window as any).api = this
    }

    get isLoggedIn(){return !!this._user }
    get name(){return this._user?.name }
    get api(){return this._api}
    get userId(){return this._user?._id }
    get rsa(){return this._crypto}
    
    init(publicKey:string = PUBLIC_PEM){
        return this._crypto.init(publicKey)
    }

    auth(){
        try {
            const token = localStorage.getItem(KEY)
            if (!token) throw 1

            const payload = jwtDecode<IUser>(token)
            const now = Date.now()/1000
            if (now>payload.exp) throw 1
            this._user = payload
            this.emit("auth", this._user)
            this._api.defaults.headers.common["jwt"] = token
            return this._user
        }catch(e){
            this.emit("auth", null)
            return false
        }
    }

    protected save(token:string){
        if (token) localStorage.setItem(KEY, token)
        else localStorage.removeItem(KEY)
    }

    protected onAuthenticated(token:string){
        this.save(token)
        this._api.defaults.headers.common["jwt"] = token
        this._user = jwtDecode<IUser>(token)
        this.emit("auth", this._user)
        return this
    }

    async login(email:string,pass:string){
        const password = await this._crypto.encrypt(await Crypto.sha256(pass))
        const {data} = await this._api.post<string>("/1/users/login", {email,password}, {timeout:1000})
        return this.onAuthenticated(data)
    }

    async register(email:string,pass:string,name:string){
        const password = await this._crypto.encrypt(await Crypto.sha256(pass))
        const {data} = await this._api.post<string>("/1/users/register", {email,password,name})
        return this.onAuthenticated(data)
    }

    getUsernames(){
        return this._api.get<IUsername[]>("/1/names").then(data => data.data)
    }
    
    logout(){
        this._user = null
        this.save("")
        this.emit("auth", null)
    }

    onError(response:AxiosResponse) {
        if (response.status==401) {
            this._user = null
            this.emit("auth", null)
        }
        throw response
    }
}